import type { ReactNode } from 'react';
import { CheckCircle2, XCircle, Clock3, CalendarRange, User2, Ban } from 'lucide-react';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Badge } from '@/shared/components/ui/Badge';
import {
  DEPT_LABELS,
  LEAVE_STATUS_LABEL,
  LEAVE_STATUS_TONE,
  LEAVE_TYPE_LABELS,
  fmtDate,
} from '../constants';
import type { Leave } from '../types';

interface LeaveDetailDrawerProps {
  leave: Leave | null;
  open: boolean;
  /** Privileged viewers (admin/superadmin) see employee identity + approval trail. */
  isPriv: boolean;
  /** Review actions available for this viewer on this leave (rendered if provided). */
  actions?: ReactNode;
  onClose: () => void;
}

/**
 * Read-only leave detail view. Shows ONLY fields the backend genuinely returns:
 * employee, type, dates, duration (days), reason, status, and the single-tier
 * approval outcome (HR Admin / Super Admin). The backend does not store reviewer
 * comments, attachments, or leave balances, so those sections are intentionally
 * absent (documented as backend limitations, never faked).
 */
export function LeaveDetailDrawer({ leave, open, isPriv, actions, onClose }: LeaveDetailDrawerProps) {
  if (!leave) return null;

  // Single-tier approval: a leave is reviewed once by an HR Admin or Super Admin.
  // Reviewer name is shown when the backend populated it; otherwise the outcome
  // (Approved/Rejected) stands on its own.
  const reviewerName = leave.sa_reviewer_name || leave.admin_reviewer_name;

  return (
    <Drawer open={open} onClose={onClose} title="Leave Request" width="w-[440px]">
      <div className="flex flex-col gap-5">
        {/* Status + type */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarRange className="h-5 w-5 text-primary" />
            <span className="text-data-lg font-semibold text-on-surface">
              {LEAVE_TYPE_LABELS[leave.type] ?? leave.type} Leave
            </span>
          </div>
          <Badge tone={LEAVE_STATUS_TONE[leave.status] ?? 'neutral'}>
            {LEAVE_STATUS_LABEL[leave.status] ?? leave.status}
          </Badge>
        </div>

        {/* Employee (privileged only) */}
        {isPriv && (
          <Field label="Employee">
            <div className="flex items-center gap-2">
              <User2 className="h-4 w-4 text-on-surface-variant" />
              <div>
                <div className="font-medium text-on-surface">{leave.employee_name ?? '—'}</div>
                <div className="text-body-sm text-on-surface-variant">
                  {DEPT_LABELS[leave.employee_dept ?? ''] ?? leave.employee_dept ?? '—'}
                </div>
              </div>
            </div>
          </Field>
        )}

        {/* Dates + duration */}
        <div className="grid grid-cols-3 gap-3">
          <Metric label="From" value={fmtDate(leave.from_date)} />
          <Metric label="To" value={fmtDate(leave.to_date)} />
          <Metric label="Duration" value={`${leave.days} day${leave.days === 1 ? '' : 's'}`} />
        </div>

        {/* Reason */}
        <Field label="Reason">
          <p className="whitespace-pre-wrap text-data-md text-on-surface">
            {leave.reason?.trim() || <span className="text-on-surface-variant">No reason provided.</span>}
          </p>
        </Field>

        {/* Submitted */}
        <Field label="Submitted">
          <span className="text-data-md text-on-surface">{fmtDate(leave.created_at)}</span>
        </Field>

        {/* Approval outcome (privileged) */}
        {isPriv && (
          <Field label="Approval">
            {leave.status === 'cancelled' ? (
              <div className="flex items-start gap-2.5">
                <Ban className="mt-0.5 h-4 w-4 shrink-0 text-on-surface-variant" />
                <div className="min-w-0">
                  <div className="font-medium text-on-surface">Cancelled by employee</div>
                  <div className="text-body-sm text-on-surface-variant">
                    {[leave.cancel_reason?.trim() || 'No reason provided', leave.cancelled_at ? fmtDate(leave.cancelled_at) : null]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </div>
              </div>
            ) : (
              <ol className="flex flex-col gap-3">
                <TrailStep
                  done={leave.status === 'approved' || leave.status === 'rejected'}
                  ok={leave.status === 'approved'}
                  title="HR Review"
                  name={reviewerName}
                  when={leave.final_reviewed_at}
                  pendingLabel="Awaiting HR review"
                />
              </ol>
            )}
          </Field>
        )}

        {/* Review actions (if the viewer can act) */}
        {actions && <div className="flex items-center gap-2 border-t border-outline pt-4">{actions}</div>}
      </div>
    </Drawer>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-label-caps font-semibold uppercase text-on-surface-variant">{label}</span>
      {children}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-card-sm border border-outline bg-surface-container-low px-3 py-2">
      <p className="text-[10px] uppercase tracking-wide text-on-surface-variant">{label}</p>
      <p className="text-data-md font-semibold text-on-surface">{value}</p>
    </div>
  );
}

function TrailStep({
  done,
  ok,
  title,
  name,
  when,
  pendingLabel,
}: {
  done: boolean;
  ok: boolean;
  title: string;
  name?: string;
  when?: string;
  pendingLabel: string;
}) {
  const Icon = !done ? Clock3 : ok ? CheckCircle2 : XCircle;
  const tone = !done ? 'text-warning' : ok ? 'text-success' : 'text-danger';
  const detail = done
    ? [ok ? 'Approved' : 'Rejected', name, when ? fmtDate(when) : null].filter(Boolean).join(' · ')
    : pendingLabel;
  return (
    <li className="flex items-start gap-2.5">
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${tone}`} />
      <div className="min-w-0">
        <div className="font-medium text-on-surface">{title}</div>
        <div className="text-body-sm text-on-surface-variant">{detail}</div>
      </div>
    </li>
  );
}
